
<template>
  <div>
    <h1>Earns Methoden</h1>
    <p>
      Maßnahmen können mit der überschlägigen oder der genormten / wissenschaftlichen
      Methode berechnet werden. Die überschlägigen Methoden benötigen weniger Daten
      und sind somit schneller zu untersuchen. Die genormten / wissenschaftlichen Methoden
      bieten auf der anderen Seite eine zuverlässige Berechnung welche belastbar sind.
    </p>
    <p>
      Um eine Maßnahme abspeichern zu können, sind in alle technischen
      Pflichtfelder valide Werte einzutragen. Zusätzlich können optional die
      finanziellen Parameter eingetragen werden.
    </p>

    <h1>Sonstige erneuerbare Energien</h1>
    <p>
      Diese Methode erlaubt es, die Ergebnisse aus einer eigenen Berechnung, Annahme
      oder einer detaillierten Berechnung, Simulation außerhalb von EARNS in Earns
      einpflegen zu können.
    </p>

    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Schritte vor der Berechnung</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ol>
            <li>
              <b>Energiemengen extern berechnen oder annehmen</b>
              <p>
                Die erzeugten und eigenverbrauchten Energiemengen (Strom, Wärme,
                Kälte) müssen außerhalb von EARNS ermittelt werden. Hierfür
                können die kostenlosen Tools aus dem Abschnitt
                "Berechnungstools" genutzt werden (PVGIS, PV*SOL Online,
                T*SOL Online).
              </p>
            </li>
            <li>
              <b>Eigenverbrauchs- und Einspeiseanteile bestimmen</b>
              <p>
                Nur der Eigenverbrauch innerhalb der Bilanzgrenze reduziert
                Energiekosten, CO2 und Primärenergie. Für eingespeiste Mengen
                wird ausschließlich die Vergütung angerechnet.
              </p>
            </li>
            <li>
              <b>Vergütungstarif prüfen</b>
              <p>
                Der Vergütungstarif für die Einspeisung kann in den
                <router-link :to="{ name: 'ProjectDetails' }">
                  Projektdetails
                </router-link>
                angepasst werden.
              </p>
            </li>
            <li>
              <b>
                Kosten für die gewählte Systemlösung ermitteln, unter
                Berücksichtigung aller relevanten Faktoren wie Aufwand für
                Demontage, Umbau, Einbringung, Anschlussarbeiten,
                Inbetriebnahme, etc.
              </b>
            </li>
            <li>
              <b>
                Finale Wirtschaftlichkeit (Einsparung, Rendite, Amortisation)
                anhand des Angebotspreises und der finalen Einsparhöhe ermitteln
              </b>
            </li>
          </ol>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungsgrundlage und Daten</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <v-container>
            <h2>Berechnungsgrundlage</h2>
            <p>
              Erneuerbare Energien sind Energien, die sich im menschlichen 
              Zeithorizont schnell erneuern und somit regenerativ sind. 
              Diese grenzen sich somit von den Fossilen Energien ab und 
              werden auch alternative Energien genannt. 
              
              Erneuerbare Energien sind für die Energiewende und -transformation
              unerlässlich. Zu ihnen zählen Bioenergie, Geothermie, Meeresenergie, 
              Wasserkraft, Sonnenenergie und Windenergie. Wobei die letzten drei 
              den größten Anteil an der Stromerzeugung haben. 
            </p>

            <p>
              Für Gebäude kommen vor allem Aufdach-Photovotaikanlagen (PV) und 
              Solarthermieanlagen (ST) in Frage. Kombiniert oder als PVT-Anlagen 
              können diese den Energieverbrauch weiter senken. Darüber hinaus 
              können Sorptionskältemaschinen mit Solarthermie und Kältemaschienen 
              oder reversible Wärmepumpen mit PV-Strom betrieben werden und somit 
              auch Kälte erneuerbar erzeugt werden.
            </p>

            <p>
              Mit dieser Methode können die vorher genannten Anlagen und Systeme 
              in EARNS aufgenommen werden. Die Energiemengen müssen für diese Methode
              extern berechnet oder angenommen werden. Hierfür können die kostenlosen
              Berechnungstools in den nächsten Abschnitten genutzt werden. 
            </p>

            <p>
              Die Berechnungsgrundlage für die erneuerbaren Energien sind die
              Bilanzgrenzen des Unternehmen oder Gebäude. Somit wird die 
              Energiekosteneinsparung, CO2-Einsparung und Primärenergieeinsparung 
              mit jedem kW an eigenverbrauchter erneuerbaren Energie erhöht. 
              Jedoch wird nur die Energiekosteneinsparung für die eingespeisten 
              erneuerbaren Energien mit dem Vergütungstarif des Netzbetreibers verrechnet. 
              Dies gilt für die Strom-, Wärme- und Kälteerzeugung. Die CO2-Einsparung 
              und Primärenergieeinsparung ist bei Einspeisung in ein externes Netz, 
              außerhalb der Bilanzgrenze und somit dürfen nicht verrechnet werden.
            </p>
          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Eingabe der Referenz- und Geplanten-Anlage</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <table>
            <caption>Tabelle: Technische Parameter</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Vorhanden (Referenz)</td>
                <td>Auswahl ob Referenzanlage vorhanden</td>
              </tr>
              <tr>
                <td>Anzahl</td>
                <td>Eingabe der Anzahl</td>
              </tr>
              <tr>
                <td>Stromeigenverbrauch</td>
                <td>Eingabe des Stromeigenverbrauchs in kWh/a</td>
              </tr>
              <tr>
                <td>Stromeinspeisung</td>
                <td>Auswahl ob vergütete Stromeinspeisung vorhanden</td>
              </tr>
              <tr>
                <td>Stromeinspeisung</td>
                <td>Eingabe der vergüteten Stromeinspeisemenge in kWh/a</td>
              </tr>
              <tr>
                <td>Wärmeeigenverbrauch</td>
                <td>Eingabe des Wärmeeigenverbrauchs in kWh/a</td>
              </tr>
              <tr>
                <td>Kälteeigenverbrauch</td>
                <td>Eingabe des Kälteeigenverbrauchs in kWh/a</td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Tabelle: Finanzielle Parameter</caption>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Demontagekosten (Referenz)</td>
                <td>
                  Gesamtkosten der Demontagekosten inkl. Arbeitszeit,
                  Maschinenkosten und Entsorgungskosten.
                </td>
              </tr>
              <tr>
                <td>Kosten Neuanlage (Geplant)</td>
                <td>Gesamte Anschaffungskosten bis hin zur Inbetriebnahme.</td>
              </tr>
              <tr>
                <td>Betriebs- und Instandhaltungskosten</td>
                <td>
                  Jährliche Kosten für Betriebsmaterial und
                  Instandhaltungsarbeiten (ohne Energiekosten).
                </td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungstools</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <table>
            <caption>Tabelle: Kostenlose Berechnungstools</caption>
            <thead>
              <tr>
                <th>Bezeichnung und Link</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  https://re.jrc.ec.europa.eu/pvg_tools/en/
                </td>
                <td>
                  PVGIS berechnet Sonneneinstrahlung und die Leistung von
                  Photovoltaikanlagen für jeden Standort.
                  Es werden reale Einstrahlungsdaten verwendet.
                </td>
              </tr>

              <tr>
                <td>https://pvsol-online.valentin-software.com/</td>
                <td>
                  PV*SOL Online berechnet eine mittlere monatliche Stromerzeugung und -einsparung 
                  durch Photovoltaik für jeden Standort.
                </td>
              </tr>

              <tr>
                <td>https://online.tsol.de/en/</td>
                <td>
                  T*SOL Online berechnet die mittlere monatliche Wärmeerzeugung und Wärmeeinsparung
                  durch Solarthermie.
                </td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Beispiele</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text eager>
          <v-container>
            <h2>Beispiele</h2>

            <h3>Photovoltaik (PV)</h3>
            <p>
              Ausbau Photovoltaik und Erhöhung der Stromeigendeckung kann durch folgenden Parametern eingegeben werden:
            </p>

            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Wert</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Vorhanden (Referenz)</td>
                  <td>Ja, falls Photovoltaik bereits vorhanden.</td>
                </tr>
                <tr>
                  <td>Anzahl</td>
                  <td>1</td>
                </tr>
                <tr>
                  <td>Stromeigenverbrauch</td>
                  <td>100.000 kWh/a</td>
                </tr>
                <tr>
                  <td>Stromeinspeisung</td>
                  <td>
                    Auswahl ob vergütete Stromeinspeisung vorhanden.<br />
                    Der Vergütungstarif kann in den
                    <router-link :to="{ name: 'ProjectDetails' }">
                      Projektdetails
                    </router-link>
                    angepasst werden.
                  </td>
                </tr>
                <tr>
                  <td>Stromeinspeisung</td>
                  <td>20.000 kWh/a</td>
                </tr>
                <tr>
                  <td>Wärmeeigenverbrauch</td>
                  <td>0 kWh/a</td>
                </tr>
                <tr>
                  <td>Kälteeigenverbrauch</td>
                  <td>0 kWh/a</td>
                </tr>
              </tbody>
            </table>

            <h3>Photovoltaik + Solarthermie (PVT)</h3>
            <p>
              Ausbau PVT und Erhöhung der Strom- und Wärmeeigendeckung kann durch folgende Parameter eingegeben werden:
            </p>

            <table>
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Wert</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Vorhanden (Referenz)</td>
                  <td>Ja, falls Photovoltaik bereits vorhanden.</td>
                </tr>
                <tr>
                  <td>Anzahl</td>
                  <td>1</td>
                </tr>
                <tr>
                  <td>Stromeigenverbrauch</td>
                  <td>100.000 kWh/a</td>
                </tr>
                <tr>
                  <td>Stromeinspeisung</td>
                  <td>
                    Auswahl ob vergütete Stromeinspeisung vorhanden.<br />
                    Der Vergütungstarif kann in den 
                    <router-link :to="{ name: 'ProjectDetails' }">
                      Projektdetails 
                    </router-link>
                    angepasst werden.
                  </td>
                </tr>
                <tr>
                  <td>Stromeinspeisung</td>
                  <td>20.000 kWh/a</td>
                </tr>
                <tr>
                  <td>Wärmeeigenverbrauch</td>
                  <td>50.000 kWh/a</td>
                </tr>
                <tr>
                  <td>Kälteeigenverbrauch</td>
                  <td>0 kWh/a</td>
                </tr>
              </tbody>
            </table>

            <p>Für komplexere Erzeugersysteme sprechen Sie uns gerne an.</p>
          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Quellen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <h2>Quellen</h2>
          <ul>
            <li>
              PVGIS – Photovoltaic Geographical Information System,
              Joint Research Centre (JRC) der Europäischen Kommission
              (https://re.jrc.ec.europa.eu/pvg_tools/en/)
            </li>
            <li>
              PV*SOL Online, Valentin Software GmbH
              (https://pvsol-online.valentin-software.com/)
            </li>
            <li>
              T*SOL Online, Valentin Software GmbH
              (https://online.tsol.de/en/)
            </li>
            <li>
              Umweltbundesamt: „Erneuerbare Energien in Deutschland"
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

  </div>
</template>

<script setup lang="ts">
defineOptions({
  name: "FreeRenewableHelp"
});
</script>

<style scoped>
</style>
